import React, { useEffect, useRef } from "react";

const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  const numCircles = 25;
  const radius = 8;
  const lineCreatedMinDistance = 120;
  const stroke = "#6c2a1b";
  let circleSpeed = 1;
  let mouse = { x: null, y: null };

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let animationFrameId;

    let circles = [];
    for (let i = 0; i < numCircles; i++) {
      circles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        dx: Math.random() < 0.5 ? 1 * Math.random() : -1 * Math.random(),
        dy: Math.random() < 0.5 ? 1 * Math.random() : -1 * Math.random(),
        radius: radius,
      });
    }

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const updateCircles = (circles) => {
      for (let i = 0; i < circles.length; i++) {
        let [x, y, dx, dy, w, h] = [
          circles[i].x,
          circles[i].y,
          circles[i].dx,
          circles[i].dy,
          window.innerWidth,
          window.innerHeight,
        ];

        if ((x > w - radius && dx > 0) || (x < radius && dx < 0)) dx *= -1;
        if ((y > h - radius && dy > 0) || (y < radius && dy < 0)) dy *= -1;

        if (mouse.x !== null && mouse.y !== null) {
          const dxMouse = mouse.x - x;
          const dyMouse = mouse.y - y;
          const distance = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);

          if (distance < 150) {
            const attractionStrength = 1 - distance / 150;
            x += dxMouse * attractionStrength * 0.1;
            y += dyMouse * attractionStrength * 0.1;
          }
        }

        x += dx * circleSpeed;
        y += dy * circleSpeed;
        circles[i].x = x;
        circles[i].dx = dx;
        circles[i].y = y;
        circles[i].dy = dy;
      }
      return circles;
    };

    const drawLinesBetweenCircles = (circles, ctx) => {
      for (let i = 0; i < circles.length; i++) {
        for (let j = i + 1; j < circles.length; j++) {
          let [x1, x2, y1, y2] = [
            circles[i].x,
            circles[j].x,
            circles[i].y,
            circles[j].y,
          ];
          const dist = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
          if (dist <= lineCreatedMinDistance) {
            const brightnessFactor = 1 - dist / lineCreatedMinDistance;
            const baseColor = { r: 142, g: 112, b: 104 };
            const brightenedColor = {
              r: Math.min(50, baseColor.r + brightnessFactor * 100),
              g: Math.min(20, baseColor.g + brightnessFactor * 100),
              b: Math.min(70, baseColor.b + brightnessFactor * 100),
            };
            const dynamicFillColor = `rgb(${brightenedColor.r}, ${brightenedColor.g}, ${brightenedColor.b})`;

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.strokeStyle = stroke;
            ctx.stroke();
            ctx.fill();

            ctx.fillStyle = dynamicFillColor;
            ctx.beginPath();
            ctx.arc(x1, y1, radius, 0, 2 * Math.PI, false);
            ctx.fill();
            ctx.closePath();
            ctx.beginPath();
            ctx.arc(x2, y2, radius, 0, 2 * Math.PI, false);
            ctx.fill();
            ctx.closePath();
          }
        }
      }
    };

    const draw = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);

      circles = updateCircles(circles);
      circles.map((item) => {
        context.beginPath();
        context.arc(item.x, item.y, item.radius, 0, 2 * Math.PI, false);
        context.strokeStyle = stroke;
        context.stroke();
        context.closePath();
        return item;
      });
      drawLinesBetweenCircles(circles, context);
      animationFrameId = requestAnimationFrame(draw);
    };

    const handleMouseMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };

    resizeCanvas();
    draw();

    window.addEventListener("resize", resizeCanvas);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas ref={canvasRef} />;
};

export default AnimatedBackground;
